/** @format */

import { isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	Input,
	OnChanges,
	OnInit,
	PLATFORM_ID,
	SimpleChange,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import { BreakpointsService, UtilityService, WINDOW } from '@shared/utils/services';
import { PDP_CONSTANTS } from '@pdp/app/app.constants';
import { chain } from '@core/chain';
import { ActivatedRoute } from '@angular/router';
import { BreakPointNew } from '@shared/data-access/enums';
import { selectSustainabilityAttributes } from '@pdp/data-access/state';
import { Store } from '@ngrx/store';
import {
	ProcessedNonBundleProduct,
	ProductSku,
	ProductStyle,
	SkuDescriptiveAttribute,
	StyleDescriptiveAttribute
} from '@shared/data-access/interfaces';
import { ProductService } from '@shared/data-access/services';

type AttributeWithIcon = StyleDescriptiveAttribute & {
	icon?: string;
};

@Component({
	selector: 'pdp-product-description',
	templateUrl: './product-description.component.html',
	styleUrls: ['./product-description.component.scss'],
	standalone: false
})
export class ProductDescriptionComponent implements OnChanges, OnInit {
	/**
	 * @description
	 * Link to the non-PLA pdp page.
	 */
	public normalPDPLink: string;
	/**
	 * @description
	 * The current Product
	 */
	@Input()
	public product: ProcessedNonBundleProduct | ProductStyle;

	/**
	 * @description
	 * Resolved SKU ID
	 */
	@Input()
	public resolvedSkuID: string;

	@Input()
	public isProductDescriptionAccordionDisabled = true;

	@Input()
	public isProductDescriptionAccordionCollapsed: boolean;

	@Input()
	public productDescriptionHideHeaderLine: boolean;

	/**
	 * @description
	 * Coming Soon End Date
	 */
	@Input()
	public comingSoonEndDate: string;

	/**
	 * @description
	 * Determines whether to show the sustainability icons and tooltips
	 */
	@Input() sustainablityIconsEnabled: boolean;

	/**
	 * @description
	 * Determines whether to show the info summary
	 */
	@Input() infoSummaryEnabled = false;

	/**
	 * @description
	 * Determines whether to show the info summary for tents and sleeping bags
	 */
	@Input() infoSummaryTentsAndSleepingBagsEnabled = false;

	@Input() isOOSProductDescription = false;

	private readonly platformId = inject(PLATFORM_ID);
	private readonly window = inject(WINDOW) as Window;
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly productService = inject(ProductService);
	private readonly route = inject(ActivatedRoute);
	private readonly utilityService = inject(UtilityService);
	private readonly store = inject(Store);
	private readonly breakpointsService = inject(BreakpointsService);

	/**
	 * @description
	 * Coming Soon boolean
	 */
	public isComingSoon: boolean;

	isMobile = true;

	selectSustainabilityAttributes$ = this.store.select(selectSustainabilityAttributes);

	/**
	 * @description
	 * Flag for expanding/collapsing the description body accordion
	 */
	public isAbbreviatedDescription: boolean;

	public isExpandable: boolean;

	/**
	 * @description
	 * Flag to display/hide the Pro Tips link
	 */
	public isProTipDisplayable = chain.proTipsFlag;

	/**
	 * @description
	 * California Proposition 65 config
	 */
	public prop65Product = {
		isprop65Product: false,
		prop65warningtext: ''
	};

	/**
	 * @description
	 * ViewChild for description text
	 */
	@ViewChild('descriptionText', { read: ViewContainerRef, static: false })
	public descriptionText: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for displayable descriptive attributes
	 */
	@ViewChild('displayableAttributes', { read: ViewContainerRef, static: false })
	public displayableAttributes: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for Proposition 65 content
	 */
	@ViewChild('propSixtyFive', { read: ViewContainerRef, static: false })
	public propSixtyFive: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for product style / SKU
	 */
	@ViewChild('productNumbers', { read: ViewContainerRef, static: false })
	public productNumbers: ViewContainerRef;

	/**
	 * @description
	 * Max Description Height for Read More button to be displayed
	 */
	readonly MAX_DESCRIPTION_HEIGHT = 171;

	/**
	 * @description
	 * Keeping track of inner witdh of screen so that we know if we should apply resize method
	 */
	private previousInnerWidth: number = null;

	/**
	 * @description
	 * List of attributes to display in the info summary box
	 */
	public infoSummary: AttributeWithIcon[] = [];

	/**
	 * @description
	 * Determines whether or not to show info summary.
	 */
	public shouldShowInfoSummary = false;

	/**
	 * @description
	 * Determines whether or not to show the icon in info summary.
	 */
	public displayInfoSummaryIcon = false;

	/**
	 * @description
	 * List of attributes to display in the specs under product information
	 */
	public displayableDescriptiveAttributes: StyleDescriptiveAttribute[] = [];

	public PRODUCT_ATTRIBUTE_IDENTIFIER = '2526';

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			effect(() => {
				const breakpoint = this.breakpointsService.breakpoint();
				const isMobileBreakpoint = breakpoint <= BreakPointNew.SMALL;
				this.onResize(isMobileBreakpoint);
			});
		}
	}

	ngOnInit(): void {
		this.setNormalPDPLink();
		this.initializeProp65Product(false);

		if (!this.product) {
			return;
		}

		this.setDisplayableAttributes();
		this.checkForProp65Warnings();

		if (this.infoSummaryEnabled) {
			this.handleInfoSummary();
		}
	}

	ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
		if (
			changes.comingSoonEndDate &&
			changes.comingSoonEndDate.currentValue !== changes.comingSoonEndDate.previousValue
		) {
			this.isComingSoon = this.productService.isProductComingSoon(
				changes.comingSoonEndDate.currentValue
			);
		}
	}

	public imagesLoaded(): void {
		// Don't reset if athlete clicked "read more" before images finished loading
		if ((this.isAbbreviatedDescription && this.isExpandable) || !this.isExpandable) {
			this.isAbbreviatedDescription = true;
		}
		this.calcDescriptionTextHeight();
	}

	public setDisplayableAttributes(): void {
		if (this.product.descriptiveAttributes) {
			this.displayableDescriptiveAttributes =
				this.productService.getDisplayableDescriptiveAttributesQV(
					this.product.descriptiveAttributes
				);
		}
	}

	public setNormalPDPLink(): void {
		const { seoToken, partNumber } = this.route.snapshot.params;
		this.normalPDPLink = `/p/${seoToken}/${partNumber}`;
	}

	public initializeProp65Product(isEnabled: boolean): void {
		this.prop65Product.isprop65Product = isEnabled;
	}

	public checkForProp65Warnings(): void {
		if ('skus' in this.product && Array.isArray(this.product.skus)) {
			this.product.skus.forEach((sku: ProductSku) => {
				this.checkProp65DescriptiveAttributes(sku.descriptiveAttributes);
			});
		}
	}

	public checkProp65DescriptiveAttributes(
		descriptiveAttributes: SkuDescriptiveAttribute[]
	): void {
		const prod65Message = descriptiveAttributes?.find(
			(attr) => attr.identifier === '4986'
		)?.value;
		if (typeof prod65Message === 'string') {
			this.initializeProp65Product(true);
			this.prop65Product.prop65warningtext = prod65Message;
		}
	}

	public handleInfoSummary(): void {
		const attributes = this.product.descriptiveAttributes || [];
		attributes.forEach((attribute) => {
			this.addToInfoSummaryCheck(attribute);
			this.showInfoSummaryCheck(attribute);
		});
	}

	/**
	 * @description
	 * Filters through the attr ids set in the constant file and compares to the ones on the product.
	 * It will push to a new array if the ids match so they can be used for display.
	 * This also appends an icon set in the constants file if the id matches the id property for the icon.
	 */

	addToInfoSummaryCheck(productAttribute: StyleDescriptiveAttribute) {
		const icons = {
			2564: 'PL-Tent-Capacity.svg',
			5576: 'PL-Tent-Weight.svg',
			1739: 'PL-Tent-Type.svg'
		};
		PDP_CONSTANTS.infoSummaryAttribute.ids.filter((attrIdsToAddToSummary: string) => {
			if (attrIdsToAddToSummary === productAttribute.identifier) {
				const addIcon = icons[attrIdsToAddToSummary];
				if (addIcon) {
					this.displayInfoSummaryIcon = true;
					productAttribute['icon'] = addIcon;
				}
				return this.infoSummary.push(productAttribute);
			}
		});
	}

	/**
	 * @description
	 * this filters through the values set in the constants infoSummaryAttribute object and  * compares the attr values on the current product page.
	 * This then sets the shouldShowInfoSummary value based on the condition.
	 */
	showInfoSummaryCheck(productAttribute: StyleDescriptiveAttribute) {
		PDP_CONSTANTS.infoSummaryAttribute.values.filter((attrValue: string) => {
			if (attrValue === productAttribute.value) {
				if (productAttribute.value === 'Mountain') {
					return (this.shouldShowInfoSummary =
						productAttribute.identifier === this.PRODUCT_ATTRIBUTE_IDENTIFIER);
				}
				return (this.shouldShowInfoSummary = true);
			}
		});
	}

	calcDescriptionTextHeight() {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		//check that elements are in DOM before accessing the height
		if (
			!this.descriptionText?.element ||
			!this.displayableAttributes?.element ||
			!this.productNumbers?.element
		) {
			return;
		}

		const getDescriptionHeight = () =>
			this.utilityService.getElementHeight(
				this.descriptionText.element.nativeElement,
				true,
				true
			);
		const getAttributesHeight = () =>
			this.utilityService.getElementHeight(
				this.displayableAttributes.element.nativeElement,
				true,
				true
			);
		const getNumbersHeight = () =>
			this.utilityService.getElementHeight(
				this.productNumbers.element.nativeElement,
				true,
				true
			);
		const getPropSixtyFiveHeight = () =>
			this.propSixtyFive
				? this.utilityService.getElementHeight(
						this.propSixtyFive.element.nativeElement,
						true,
						true
					)
				: 0;

		const newDescriptionTextHeight = getDescriptionHeight();
		const newDisplayableAttributesHeight = getAttributesHeight();
		const newProductNumbersHeight = getNumbersHeight();
		const newPropSixtyFiveHeight = getPropSixtyFiveHeight();

		const combinedHeight =
			newDescriptionTextHeight +
			newDisplayableAttributesHeight +
			newProductNumbersHeight +
			newPropSixtyFiveHeight;

		this.isExpandable = combinedHeight >= this.MAX_DESCRIPTION_HEIGHT;

		if (!this.isExpandable) {
			this.isAbbreviatedDescription = false;
		}
	}

	public onResize(isMobileBreakpoint: boolean): void {
		const width = this.window.innerWidth;
		if (this.previousInnerWidth === width) {
			// Return if there has been no change in screen width
			return;
		}

		// Set previous inner width
		this.previousInnerWidth = width;
		// Call to get the breakpoint only once, and only force recalculate on resize!
		this.isMobile = isMobileBreakpoint;

		this.changeDetectorRef.detectChanges();

		// this must be set before calcDescriptionTextHeight()
		this.isAbbreviatedDescription = true;

		this.calcDescriptionTextHeight();
	}

	trackByName(index: number, item: StyleDescriptiveAttribute): string {
		return index.toString() + item?.name;
	}

	trackByAttribute(index: number, item: StyleDescriptiveAttribute): string {
		return index.toString() + item?.name + item?.value;
	}

	handleAccordionChanges(active: boolean) {
		if (active) {
			this.isAbbreviatedDescription = true;
			this.calcDescriptionTextHeight();
		}
	}

	onReadMoreClicked() {
		this.isAbbreviatedDescription = false;
		this.changeDetectorRef.detectChanges();
	}
}
